import { createRef, useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { map, chain, isEmpty, find, noop, size, first } from "lodash"
import { Popover, MenuList, MenuItem, Typography, TextField, InputAdornment, Box } from "@mui/material"
import Tooltip from "components/material/tooltip"
import { Search, Info } from "@mui/icons-material"
import NewFeature from "modules/new-feature"
import { SEARCHABLE_MENU_ITEM } from "helpers/shapes"
import { getHighlightedText } from "helpers/menu"
import { createMatch } from "helpers/string"

const DEFAULT = {
  search: "",
  selected: null,
}

export default function SearchableSelector({
  items,
  current,
  onSelect,
  onClickAway,
  className,
  placeholder = "Search...",
  noResultsText = "No items match your search",
  isOpen,
  anchorElement,
  header = "Search",
}) {
  const [search, setSearch] = useState(DEFAULT.search)
  const [selected, setSelected] = useState(DEFAULT.selected)

  const firstItem = useRef(createRef())

  const match = createMatch(search)

  const orderedItems = chain(items)
    .filter(({ label }) => isEmpty(search) || match(label))
    .orderBy(["order", "asc"])
    .value()

  useEffect(() => {
    const item = find(items, ({ id }) => id === current)
    setSelected(item)
  }, [items, current])

  useEffect(() => {
    if (isOpen) {
      setSearch(DEFAULT.search)
    }
  }, [isOpen])

  const handleClickAway = useCallback(() => {
    if (onClickAway) {
      onClickAway()
    }
  }, [onClickAway])

  const handleKeyDown = useCallback((e) => {
    const { key } = e
    if (key === "Enter" && size(orderedItems) === 1) {
      const item = first(orderedItems)
      setSelected(item)
      onSelect(item)
    } else if (key === "ArrowDown" && firstItem.current) {
      firstItem.current.focus()
    }
  }, [orderedItems, onSelect])

  const onChange = (e) => {
    const value = e.target.value
    setSearch(value)
  }

  const makeClick = (item) => () => {
    setSelected(item)
    onSelect(item)
  }

  if (!isOpen) {
    return null
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      classes={{ root: className }}
      className="searchable-menu-popover"
      onClose={handleClickAway}
    >
      <div className="searchable-selector-container">
        <Typography variant="subtitle1" className="header">{header}</Typography>
        <Box>
          <TextField
            sx={{ mb: 1 }}
            autoFocus
            variant="standard"
            size="small"
            placeholder={placeholder}
            value={search}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
          {isEmpty(orderedItems) && (
            <Typography tabIndex={-1} variant="subtitle2" className="no-items">
              {noResultsText}
            </Typography>
          )}
          <MenuList className="menu-list" id="menu-list-grow">
            {map(orderedItems, (item, i) => {
                const fragments = []
                if (i === 0 || (i > 0 && item.groupId !== orderedItems[i - 1].groupId)) {
                fragments.push(
                  <Typography className="group-label" color="text.secondary" key={`${item.groupId}-${i}`} tabIndex={-1}>
                    {item.groupLabel}
                  </Typography>,
                )
            }
            let link = (
              <>
                {getHighlightedText(item.label, search)}
                {item.tooltip && <Tooltip title={item.tooltip}><Info fontSize="small" /></Tooltip>}
              </>
            )
            if (item.newFeature) {
              link = <NewFeature id={item.newFeature}>{link}</NewFeature>
            }

            const isSelected = selected && item.id === selected.id
            const fragment = (
              <MenuItem
                key={item.id}
                ref={i === 0 ? firstItem : null}
                disabled={item.disabled || item.isUnsupported}
                selected={isSelected}
                tabIndex={0}
                onClick={(!item.disabled && !item.isUnsupported) ? makeClick(item) : noop}
              >
                {link}
              </MenuItem>
            )

            fragments.push(fragment)

            return fragments
          })}
          </MenuList>
        </Box>
      </div>
    </Popover>
  )
}

SearchableSelector.propTypes = {
  current: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClickAway: PropTypes.func,
  className: PropTypes.string,
  items: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM),
  placeholder: PropTypes.string,
  noResultsText: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  anchorElement: PropTypes.object.isRequired,
  header: PropTypes.string,
}
